import { useState, useEffect, useCallback } from 'react';
import izenuAPI from '../../services/izenuAPI';

// Marketing → Pré-inscrições → E-mails automáticos.
// Daily follow-up e-mails to the 100mil pre-registrations (cron/campaign_followup.php).
// The switch here is what lets the cron send; previews and tests work with it off.

export const FOLLOWUP_STAGES = [
  { key: 'no_account', label: 'Sem conta', hint: 'Pré-inscrito, sem conta na Izenu' },
  { key: 'no_release', label: 'Sem EP/álbum', hint: 'Tem conta, nunca criou um EP/álbum' },
  { key: 'no_tracks', label: 'Sem faixas', hint: 'Criou o EP/álbum com 0 ou 1 faixa' },
  { key: 'not_published', label: 'Por publicar', hint: 'Tem 2+ faixas, ainda em rascunho' },
];
export const stageLabel = (k) => (FOLLOWUP_STAGES.find((s) => s.key === k) || {}).label || k;

const fmtDay = (s) => new Date(`${s}T00:00:00`).toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });

const box = { background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: 20, marginBottom: 20 };
const h3 = { fontSize: 16, fontWeight: 700, color: '#333', margin: '0 0 12px' };
const sel = { padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6, fontSize: 14 };
const btn = (primary, disabled) => ({
  padding: '8px 16px', borderRadius: 6, border: primary ? 'none' : '1px solid #6C3FC5', fontWeight: 600, fontSize: 14,
  background: primary ? '#6C3FC5' : '#fff', color: primary ? '#fff' : '#6C3FC5',
  cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
});

export default function CampaignFollowupPanel() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [previewStage, setPreviewStage] = useState('no_account');
  const [previewStep, setPreviewStep] = useState(1);
  const [preview, setPreview] = useState(null);

  const [testEmail, setTestEmail] = useState(() => {
    try { return localStorage.getItem('followup_test_email') || ''; } catch { return ''; }
  });
  const [testScope, setTestScope] = useState('preview');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');

  const load = useCallback(async () => {
    try {
      const r = await izenuAPI.get('/admin/campaign/followup.php');
      setData(r.data);
    } catch (e) {
      setError('Não foi possível carregar o resumo.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let off = false;
    setPreview(null);
    izenuAPI.get('/admin/campaign/followup.php', { params: { preview: previewStage, step: previewStep } })
      .then((r) => !off && setPreview(r.data))
      .catch(() => !off && setPreview({ html: null, message: 'Não foi possível gerar a pré-visualização.' }));
    return () => { off = true; };
  }, [previewStage, previewStep]);

  const toggle = async () => {
    const next = !data.enabled;
    if (next && !window.confirm(
      `Ligar o envio automático?\n\nO próximo envio diário manda até ${data.per_run} emails (há ${data.due_total} por enviar agora). Continua a ser possível desligar a qualquer momento.`
    )) return;
    setSaving(true);
    setError('');
    try {
      const r = await izenuAPI.post('/admin/campaign/followup.php', { action: 'enable', enabled: next });
      setData((d) => ({ ...d, enabled: r.data.enabled }));
    } catch (e) {
      setError(e.response?.data?.message || 'Não foi possível mudar o interruptor.');
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setTestResult('');
    try { localStorage.setItem('followup_test_email', testEmail); } catch {}
    const scope = testScope === 'preview'
      ? { stage: previewStage, step: previewStep }
      : testScope === 'all1' ? { stage: 'all', step: 1 } : { stage: 'all', step: 'all' };
    try {
      const r = await izenuAPI.post('/admin/campaign/followup.php', { action: 'test', email: testEmail, ...scope });
      const res = r.data.results || [];
      const failed = res.filter((x) => x.error);
      setTestResult(failed.length
        ? `Enviados ${res.length - failed.length} de ${res.length}. Falhas: ${failed.map((x) => `${stageLabel(x.stage)} ${x.step} (${x.error})`).join('; ')}`
        : `Enviado${res.length > 1 ? 's' : ''} ${res.length} email${res.length > 1 ? 's' : ''} de teste para ${testEmail}.`);
    } catch (e) {
      setTestResult(e.response?.data?.message || 'Não foi possível enviar o teste.');
    } finally {
      setTesting(false);
    }
  };

  if (!data) return <p style={{ color: error ? '#C62828' : '#666' }}>{error || 'A carregar…'}</p>;

  const dueFor = (stage) => Object.values(data.due?.[stage] || {}).reduce((a, b) => a + b, 0);
  const days = [];
  (data.by_day || []).forEach((r) => {
    let d = days.find((x) => x.day === r.day);
    if (!d) days.push((d = { day: r.day, sent: 0, failed: 0, stages: {} }));
    d.sent += Number(r.sent);
    d.failed += Number(r.failed);
    d.stages[r.stage] = Number(r.sent);
  });

  const th = { textAlign: 'left', padding: '8px', fontSize: 12, color: '#666', fontWeight: 600, borderBottom: '1px solid #e5e5e5' };
  const td = { padding: '8px', fontSize: 13, color: '#333', borderBottom: '1px solid #f0f0f0' };

  return (
    <div>
      {error && <p style={{ color: '#C62828', fontSize: 14 }}>{error}</p>}

      {!data.installed && (
        <div style={{ padding: 16, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, marginBottom: 20, fontSize: 14 }}>
          As tabelas ainda não existem — correr <code>php bin/migration_campaign_emails.php</code> no servidor.
        </div>
      )}

      <div style={{ ...box, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <button
          onClick={toggle}
          disabled={saving || !data.installed}
          aria-pressed={data.enabled}
          style={{
            width: 52, height: 28, borderRadius: 14, border: 'none', position: 'relative', flexShrink: 0,
            background: data.enabled ? '#2E7D32' : '#ccc', cursor: saving ? 'default' : 'pointer',
          }}
        >
          <span style={{ position: 'absolute', top: 3, left: data.enabled ? 27 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
        </button>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: data.enabled ? '#2E7D32' : '#555' }}>
            Envio automático {data.enabled ? 'ligado' : 'desligado'}
          </div>
          <div style={{ fontSize: 13, color: '#777', marginTop: 2 }}>
            {data.ended
              ? 'A campanha terminou — já não são enviados emails.'
              : data.enabled
                ? `Todos os dias às 11h (Luanda), até ${data.per_run} emails por dia.`
                : 'Nenhum email é enviado aos artistas. A pré-visualização e os testes funcionam na mesma.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 24, fontSize: 13, color: '#555' }}>
          <div><div style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{data.due_total}</div>por enviar</div>
          <div><div style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{data.sent_total}</div>enviados</div>
          <div><div style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{data.optouts}</div>cancelaram</div>
        </div>
      </div>

      <div style={box}>
        <h3 style={h3}>Fases</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          {FOLLOWUP_STAGES.map((s) => (
            <div key={s.key} style={{ flex: '1 1 180px', border: '1px solid #eee', borderRadius: 8, padding: '12px 14px' }}>
              <div style={{ fontWeight: 600, color: '#333' }}>{s.label}</div>
              <div style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>{s.hint}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: '#222' }}>{data.stages?.[s.key] ?? 0}</div>
              <div style={{ fontSize: 12, color: '#6C3FC5' }}>
                {dueFor(s.key)} por enviar
                {Object.keys(data.due?.[s.key] || {}).length > 0 && ' (' + Object.entries(data.due[s.key]).map(([st, n]) => `${st}.º: ${n}`).join(', ') + ')'}
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, color: '#999', margin: '12px 0 0' }}>
          Até 3 emails por fase: o 1.º ao entrar na fase, o 2.º 3 dias depois, o 3.º 7 dias depois. Nunca dois emails à mesma
          pessoa em menos de 2 dias. Quem publica, é marcado como Concluído/Descartado ou cancela, deixa de receber.
          {data.no_email > 0 && ` ${data.no_email} pré-inscrição(ões) não têm email.`}
        </p>
      </div>

      <div style={box}>
        <h3 style={h3}>Pré-visualização e teste</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 12 }}>
          <select value={previewStage} onChange={(e) => setPreviewStage(e.target.value)} style={sel}>
            {FOLLOWUP_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value={previewStep} onChange={(e) => setPreviewStep(Number(e.target.value))} style={sel}>
            <option value={1}>1.º email</option>
            <option value={2}>2.º email (lembrete)</option>
            <option value={3}>3.º email (último)</option>
          </select>
          {preview?.sample && (
            <span style={{ fontSize: 12, color: '#999' }}>Exemplo com os dados de “{preview.sample.artist_name}” (#{preview.sample.lead_id})</span>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="O teu email"
            style={{ ...sel, minWidth: 240 }}
          />
          <select value={testScope} onChange={(e) => setTestScope(e.target.value)} style={sel}>
            <option value="preview">Só este email</option>
            <option value="all1">O 1.º email das 4 fases</option>
            <option value="all">Os 12 emails (4 fases × 3)</option>
          </select>
          <button onClick={sendTest} disabled={testing || !testEmail} style={btn(false, testing || !testEmail)}>
            {testing ? 'A enviar…' : 'Enviar teste'}
          </button>
          {testResult && <span style={{ fontSize: 13, color: '#555' }}>{testResult}</span>}
        </div>

        {preview?.subject && (
          <div style={{ fontSize: 14, marginBottom: 8 }}>
            <span style={{ color: '#999' }}>Assunto:</span> <strong>{preview.subject}</strong>
          </div>
        )}
        {preview === null ? (
          <p style={{ color: '#666' }}>A gerar…</p>
        ) : preview.html ? (
          <iframe
            title="Pré-visualização do email"
            srcDoc={preview.html}
            sandbox=""
            style={{ width: '100%', maxWidth: 620, height: 900, border: '1px solid #eee', borderRadius: 8 }}
          />
        ) : (
          <p style={{ color: '#999' }}>{preview.message}</p>
        )}
      </div>

      <div style={box}>
        <h3 style={h3}>Envios dos últimos 30 dias</h3>
        {!days.length ? (
          <p style={{ color: '#999', fontSize: 14, margin: 0 }}>Ainda não foi enviado nenhum email.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Dia</th>
                {FOLLOWUP_STAGES.map((s) => <th key={s.key} style={th}>{s.label}</th>)}
                <th style={th}>Total</th>
                <th style={th}>Falhas</th>
              </tr>
            </thead>
            <tbody>
              {days.map((d) => (
                <tr key={d.day}>
                  <td style={td}>{fmtDay(d.day)}</td>
                  {FOLLOWUP_STAGES.map((s) => <td key={s.key} style={td}>{d.stages[s.key] || 0}</td>)}
                  <td style={{ ...td, fontWeight: 600 }}>{d.sent}</td>
                  <td style={{ ...td, color: d.failed ? '#C62828' : '#999' }}>{d.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {data.failures?.length > 0 && (
          <details style={{ marginTop: 12, fontSize: 13 }}>
            <summary style={{ cursor: 'pointer', color: '#C62828' }}>Últimas falhas ({data.failures.length})</summary>
            {data.failures.map((x, i) => (
              <div key={i} style={{ color: '#555', marginTop: 4 }}>
                {x.sent_at} · {x.email} · {stageLabel(x.stage)} {x.step}.º · {x.error}
              </div>
            ))}
          </details>
        )}
      </div>
    </div>
  );
}
